import * as React from 'react';
import { cva } from 'class-variance-authority';

import { cn } from './cn';
import { Heading, Text } from './Typography';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { resolveSemanticFeedback } from './semanticFeedback';
import { WM_FLOATING_ELEVATION_CLASSNAME } from './surfaceStyles';

// Closed feedback banner. One export, fixed structure: a tone (which
// drives both surface paint and a semantic icon), a required title, and
// an optional description. Optional dismissibility (`dismissible`) and
// auto-dismiss (`duration`) are orthogonal.
//
// Self-managed dismiss: clicking the X (or the duration timer firing)
// flips an internal `dismissed` flag and the component returns null. To
// re-show after dismiss, force a remount via `key={attempt}`.
//
// No internal entry/exit animation. The Alert paints; whoever delivers
// it animates if they want to. Inline consumers wrap with
// AnimatePresence externally; the Toast pipeline (notify) lets Sonner
// own animation entirely.
//
// `elevated` paints a soft shadow for floating contexts (the only real
// caller is the Toast pipeline). Default false.

const alertSurfaceVariants = cva(
    'tw-relative tw-w-full tw-rounded-lg tw-border tw-border-solid tw-p-4',
    {
        variants: {
            tone: {
                info: 'tw-border-info-border tw-bg-info-surface tw-text-info',
                success: 'tw-border-success-border tw-bg-success-surface tw-text-success',
                warning: 'tw-border-warning-border tw-bg-warning-surface tw-text-warning',
                destructive: 'tw-border-destructive-border tw-bg-destructive-surface tw-text-destructive'
            }
        }
    }
);

const Alert = React.forwardRef(({
    className: _className,
    style: _style,
    description,
    dismissible = false,
    duration = 0,
    elevated = false,
    onDismiss,
    title,
    tone = 'info'
}, ref) => {
    if (!title) {
        throw new Error('Alert: `title` is required.');
    }

    const [dismissed, setDismissed] = React.useState(false);

    const handleDismiss = React.useCallback(() => {
        setDismissed(true);
        onDismiss?.();
    }, [onDismiss]);

    React.useEffect(() => {
        if (!duration || dismissed) {
            return undefined;
        }

        const timer = window.setTimeout(handleDismiss, duration);

        return () => window.clearTimeout(timer);
    }, [duration, dismissed, handleDismiss]);

    if (dismissed) {
        return null;
    }

    const { icon: semanticIcon } = resolveSemanticFeedback(tone);

    return (
        <div
            ref={ref}
            role="alert"
            className={cn(
                alertSurfaceVariants({ tone }),
                elevated ? WM_FLOATING_ELEVATION_CLASSNAME : 'tw-shadow-none'
            )}
        >
            <div className="tw-flex tw-items-center tw-gap-2">
                <Icon icon={semanticIcon} size="small" tone="inherit" />
                <Heading as="h5" tone="inherit" variant="title-sm">{title}</Heading>
                {dismissible ? (
                    <div className="tw-ml-auto">
                        <IconButton
                            appearance="ghost"
                            aria-label="Dismiss"
                            icon="close"
                            onClick={handleDismiss}
                            size="tiny"
                            tone={tone}
                        />
                    </div>
                ) : null}
            </div>
            {description ? (
                <div className="tw-mt-2">
                    <Text tone="inherit" variant="body">{description}</Text>
                </div>
            ) : null}
        </div>
    );
});
Alert.displayName = 'Alert';

export { Alert };
